import ajax from "superagent";
import {
  Album,
  EventType,
  PermissionFilterType,
  PhotoFilterType,
  CreateLibraryFolderParamType,
  Folder,
  SrcType,
  DestFolderType
} from "../components/types";
import { addParamToURL } from "../../../utils/BrowserHelper";
import toastr from "../../common/toastr";
import {
  decodeEntityPath,
  getEncodedEntityPath,
  getEntitySegment,
  findEntityPath
} from "../components/FolderFeatures/EncryptionOfFolder";

export const fetchInvitedEvents = async () => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/invited_events`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    return { events: data.events || [], meta: data.meta || { page: 1, nextPage: null } };
  } catch (error) {
    console.error("Failed to fetch invited events:", error);
    return { events: [], meta: { page: 1, nextPage: null } };
  }
};

export const fetchFoldersList = async (page: number = 1) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=root&content_type=library_folders&page=${page}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    return { entities: data.entities, meta: data.meta };
  } catch (error) {
    console.error("Failed to fetch folders:", error);
    return { entities: [], meta: { page: 1, nextPage: null } };
  }
};

export const fetchFolderDetails = async (
  folderId: number,
  typeOrEncodedString?: string
): Promise<Folder | null> => {
  try {
    // If typeOrEncodedString is "LibraryFolderReference", use referenceId param
    // Otherwise treat it as an encoded path string
    const isReference = typeOrEncodedString === "LibraryFolderReference";
    const paramName = isReference ? "referenceId" : "id";
    const encodedPath =
      !isReference && typeOrEncodedString ? `&p=${typeOrEncodedString}` : "";

    const response = await fetch(
      `/api/v1/photo-manager/details/?t=LibraryFolder&${paramName}=${folderId}${encodedPath}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error("Failed to fetch folder details:", error);
    return null;
  }
};

export const fetchSubFolders = async (
  folderId: number,
  page: number = 1,
  type?: string
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=LibraryFolder&${
        type === "LibraryFolderReference" ? "referenceId=" : "id="
      }${folderId}&content_type=library_folders&page=${page}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    return { entities: data.entities, meta: data.meta };
  } catch (error) {
    console.error("Failed to fetch subfolders:", error);
    return { entities: [], meta: { page: 1, nextPage: null } };
  }
};

export const fetchFolderEvents = async (
  folderId: number,
  page: number = 1,
  type?: string
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=LibraryFolder&${
        type === "LibraryFolderReference" ? "referenceId=" : "id="
      }${folderId}&content_type=galleries&page=${page}&limit=50`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error("Failed to fetch folder events:", error);
    return [];
  }
};

export const fetchFolderEventsWithPagination = async (
  folderId: number,
  page: number = 1,
  type?: string
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=LibraryFolder&${
        type === "LibraryFolderReference" ? "referenceId=" : "id="
      }${folderId}&content_type=galleries&page=${page}&limit=50`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    return {
      events: data.entities,
      meta: data.meta
    };
  } catch (error) {
    console.error("Failed to fetch folder events:", error);
    return {
      events: [],
      meta: { page: 1, nextPage: null }
    };
  }
};

export const fetchEventList = async (
  page: number,
  setEvents: (val: EventType[], nextPage: number | null) => void,
  filter: string | null = "all"
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=root&content_type=galleries&page=${page}&event_timing_scope=${filter}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    const meta = data.meta;
    setEvents(data.entities, meta.nextPage);
  } catch (error) {
    console.error("Failed to fetch events:", error);
  }
};

export const fetchReferenceEntityDetails = async (
  entityId: number,
  encodedString: string
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/details?t=LibraryFolderReference&referenceId=${entityId}&p=${encodedString}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error(`Failed to fetch albums for event ${entityId}:`, error);
    return [];
  }
};

export const fetchEventBreadcrumbDetails = async (
  eventId: number,
  referenceId: number | null,
  encodedString: string
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/details?t=Gallery&${
        referenceId ? "referenceId" : "id"
      }=${referenceId || eventId}&p=${encodedString}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error(`Failed to fetch albums for event ${eventId}:`, error);
    return [];
  }
};

export const fetchAlbumDetails = async (
  albumId: number,
  encodedString: string
) => {
  if (!albumId) return;

  try {
    const paramName = "id";

    const response = await fetch(
      `/api/v1/photo-manager/details?t=Album&${paramName}=${albumId}&p=${encodedString}`
    );

    if (!response.ok) throw new Error("Failed to fetch album data");

    const data = await response.json();
    return data.entities;
  } catch (error) {
    console.error("Error fetching album details:", error);
  }
};

export const fetchGalleryContent = async (
  eventId: number,
  reference?: string
) => {
  try {
    const response = await fetch(
      `/api/v1/photo-manager/contents?t=Gallery&${
        reference ? "referenceId" : "id"
      }=${eventId}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    const albums = data.entities || [];

    if (!albums.length) return albums;

    const mobileCameraOnlyUpload = albums[0]?.mobileCameraOnlyUpload ?? false;

    return albums.map((album: any) => ({
      ...album,
      mobileCameraOnlyUpload:
        album.mobileCameraOnlyUpload ?? mobileCameraOnlyUpload
    }));
  } catch (error) {
    console.error(`Failed to fetch albums for event ${eventId}:`, error);
    return [];
  }
};

export const fetchAlbumPhotos = async (
  albumId: number,
  page: number,
  filters?: PhotoFilterType
) => {
  try {
    const params = new URLSearchParams({
      t: "Album",
      id: albumId.toString(),
      page: page.toString()
    });

    if (filters) {
      // Add permission filters
      if (filters.permission_filters.length) {
        filters.permission_filters.forEach((filter: PermissionFilterType) => {
          if (filter.value !== null) {
            params.append(
              "permission_filters[][attribute_id]",
              filter.attribute_id.toString()
            );
            params.append(
              "permission_filters[][value]",
              filter.value.toString()
            );
          }
        });
      }
      // Add redaction filter
      if (filters.redacted_filter !== null)
        params.append("redacted_filter", filters.redacted_filter.toString());
    }

    const url = `/api/v1/photo-manager/contents?${params.toString()}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Failed to fetch photos for album ${albumId}:`, error);
    return [];
  }
};

export const fetchEventsByName = async (name: string) => {
  try {
    const response = await fetch(`/api/v1/photo-manager/search?q=${name}`);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Failed to fetch events with the name ${name}:`, error);
    return [];
  }
};

interface UnifiedSearchResponse {
  data: {
    photos: any[];
    categories: {
      tags: any[];
      libraryEntities: any[];
      groups: any[];
      people: any[];
    };
  };
  meta: {
    page: number;
    totalCount: number;
    hasMore: boolean;
    countsByType: {
      photos: number;
      tags: number;
      libraryEntities: number;
      groups: number;
      people: number;
    };
  };
}

interface UnifiedSearchOptions {
  page?: number;
  limit?: number;
  showAll?: boolean;
}

export const fetchUnifiedSearch = async (
  query: string,
  options: UnifiedSearchOptions = {}
): Promise<UnifiedSearchResponse> => {
  const { page = 1, limit = 20, showAll = false } = options;

  try {
    const params = new URLSearchParams({
      q: query,
      page: page.toString(),
      limit: limit.toString()
    });

    if (showAll) {
      params.append("show_all", "true");
    }

    const response = await fetch(
      `/api/v1/unified-search?${params.toString()}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(
      `Failed to fetch unified search results for "${query}":`,
      error
    );
    return {
      data: {
        photos: [],
        categories: {
          tags: [],
          libraryEntities: [],
          groups: [],
          people: []
        }
      },
      meta: {
        page, // Use the requested page parameter instead of hardcoded 1
        totalCount: 0,
        hasMore: false,
        countsByType: {
          photos: 0,
          tags: 0,
          libraryEntities: 0,
          groups: 0,
          people: 0
        }
      }
    };
  }
};

export const createGalleryAPI = async (
  name: string,
  folderId: number | null
): Promise<EventType> => {
  const response = await fetch("/api/v1/events/free", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      name: name.trim(),
      library_folder_id: folderId
    })
  });

  if (!response.ok) throw new Error("Failed to create event");

  const responseData = await response.json();
  if (!responseData.event) {
    throw new Error("Event property is missing in the response");
  }
  return responseData.event;
};

export const updateEventAPI = async (
  eventId: number,
  updates: {
    name?: string;
    library_folder_id?: number | null;
    publish_mode?: string;
    password?: string;
  }
): Promise<EventType> => {
  const response = await fetch(`/api/v1/events/${eventId}/update`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ id: eventId, ...updates })
  });

  if (!response.ok) {
    throw new Error("Failed to update event");
  }

  const responseData = await response.json();
  if (!responseData.event) {
    throw new Error("Event property is missing in the response");
  }
  return responseData.event;
};

export const deleteEventAPI = async (eventId: number): Promise<void> => {
  const response = await fetch(`/api/v1/events/${eventId}`, {
    method: "DELETE"
  });

  if (!response.ok) {
    throw new Error("Failed to delete event");
  }
};

export const handleAddShortcut = async (
  sourceParentFolderId: number | null,
  targetParentFolderId: number | null,
  sourceEntityType: "LibraryFolder" | "Event",
  sourceEntityId: number,
  setLoading: (val: boolean) => void
) => {
  if (sourceParentFolderId === targetParentFolderId) {
    toastr.error("Cannot create shortcut in the same parent folder");
    return;
  }

  if (sourceParentFolderId === null && targetParentFolderId === null) {
    toastr.error("Root folders cannot create shortcuts in root directory");
    return;
  }

  setLoading(true);

  try {
    const requestBody = {
      parent_folder_id: targetParentFolderId,
      entity_type: sourceEntityType,
      entity_id: sourceEntityId
    };

    const response = await fetch("/api/v1/library_folder_references", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();

    if (response.ok && data.success) {
      toastr.success("Shortcut added successfully");
      return data.reference;
    } else {
      const errorMessage = data.error || "Failed to create shortcut";
      toastr.error(errorMessage);
    }
  } catch (error) {
    console.error("Error creating shortcut:", error);
    toastr.error("Failed to create shortcut. Please try again.");
  } finally {
    setLoading(false);
  }
};

export const deleteLibraryShortcut = async (referenceId: number) => {
  try {
    const res = await ajax.delete(
      `/api/v1/library_folder_references/${referenceId}`
    );

    if (res.body?.success) {
      return { success: true, message: res.body.message };
    } else {
      return { success: false, error: res.body.error || "Unknown error" };
    }
  } catch (error: any) {
    console.error("Failed to delete shortcut:", error);
    return {
      success: false,
      error: error?.response?.body?.error || "Something went wrong"
    };
  }
};

export const handleCreateAlbum = async (
  albumName: string,
  groupPhotos: boolean,
  selectedEventId: number,
  paramType: "referenceId" | undefined,
  groupPhotosEnabled: boolean,
  encodedPath: string,
  handleURLChange: () => void,
  onClose: () => void,
  eventData?: any,
  context?: any
): Promise<any> => {
  try {
    const paramKey = paramType === "referenceId" ? "reference_id" : "event_id";

    const response = await ajax.post(
      `/api/v1/albums?${paramKey}=${selectedEventId}&name=${albumName}${
        groupPhotosEnabled ? `&group_photos=${groupPhotos}` : ""
      }&p=${encodedPath}`
    );

    if (!response.ok) {
      throw new Error("Failed to create album");
    }

    const newAlbum = response.body.album;

    const albumSegment = getEntitySegment({
      id: newAlbum.id,
      referenceId: null,
      isReference: false,
      type: "Album"
    });

    // Find the parent event path for the album
    let parentEventPath = "";

    // If we're creating an album from the sidebar, construct the path to the parent event
    if (paramType !== "referenceId") {
      const eventSegment = `g:${selectedEventId}`;

      const isRootEvent = context?.eventsList?.some(
        event => event.id === selectedEventId
      );

      if (isRootEvent) {
        // Root event: completely ignore current path, just use the event segment
        parentEventPath = eventSegment;
      } else {
        // Nested event: use findEntityPath to get the proper folder hierarchy

        if (context?.rootFolders && context.rootFolders.length > 0) {
          const eventPath = findEntityPath(
            context.rootFolders,
            selectedEventId,
            "event"
          );

          if (eventPath) {
            parentEventPath = eventPath;
          } else {
            if (encodedPath && encodedPath !== "null") {
              const decodedPath = decodeEntityPath(encodedPath);

              if (
                decodedPath &&
                typeof decodedPath === "string" &&
                decodedPath.length > 0
              ) {
                const segments = decodedPath.split("/");
                const folderSegments = segments.filter(
                  segment =>
                    segment &&
                    typeof segment === "string" &&
                    segment.length > 2 &&
                    segment.includes(":") &&
                    !segment.startsWith("g:") &&
                    !segment.startsWith("a:")
                );

                if (folderSegments.length > 0) {
                  parentEventPath = [...folderSegments, eventSegment].join("/");
                } else {
                  parentEventPath = eventSegment;
                }
              } else {
                parentEventPath = eventSegment;
              }
            } else {
              parentEventPath = eventSegment;
            }
          }
        } else {
          parentEventPath = eventSegment;
        }
      }
    } else {
      // For reference events, use the reference segment
      const referenceSegment = `r:${selectedEventId}`;
      parentEventPath = referenceSegment;
    }

    const updatedPath = `${parentEventPath}/${albumSegment}`;
    const newEncodedPath = getEncodedEntityPath(updatedPath);

    const url = new URL(window.location.href);
    url.searchParams.set("p", newEncodedPath);
    window.history.pushState({}, "", url.toString());

    handleURLChange();

    onClose();

    return newAlbum;
  } catch (error) {
    console.error("Error creating album:", error);
    toastr.error("Unable to create album. Please try again", null, 1000);
    throw error;
  }
};

export const submitAlbumName = (
  e: any,
  albumId: number,
  albumName: string,
  hasInvalidatedValues: Function,
  setAlbumNameDrafts: Function,
  setAlbumsInEditMode: Function,
  onEditSuccess: (albums: Album[] | Album) => void,
  albumsData: Album[]
) => {
  e.stopPropagation();
  if (hasInvalidatedValues(albumId, true)) {
    return;
  }

  fetch(`/api/v1/album/${albumId}/`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ name: albumName })
  })
    .then(response => {
      if (!response.ok) {
        const errorMsg =
          response.status === 404
            ? "Album not found."
            : "Failed to update album name.";
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
      return response.json();
    })

    .then(updatedAlbum => {
      setAlbumNameDrafts((prev: any) => {
        const updatedNames = { ...prev };
        delete updatedNames[albumId];
        return updatedNames;
      });

      setAlbumsInEditMode((prev: any[]) =>
        prev.filter((id: number) => id !== albumId)
      );

      if (albumsData.length > 0) {
        const updatedAlbums = albumsData.map(album =>
          album.id === albumId
            ? { ...album, name: updatedAlbum.album.name }
            : album
        );
        onEditSuccess(updatedAlbums);
      } else {
        onEditSuccess(updatedAlbum.album);
      }
    })
    .catch(error => {
      console.error("Error while updating album name:", error);
    });
};

export const handleDelete = async (
  setLoading: Function,
  albumId: number,
  onDeleteSuccess: Function
) => {
  setLoading(true);
  try {
    const response = await ajax.delete(`/api/v1/albums/${albumId}`);
    if (!response.ok) {
      throw new Error("Failed to delete album");
    }
    onDeleteSuccess();
  } catch (error) {
    console.error("Error deleting album:", error);
    // @ts-ignore
    toastr.error("Unable to delete album. Please try again", null, 1000);
  } finally {
    setLoading(false);
  }
};

export const deletePhotos = async (
  selectedMediaId: Array<number>,
  onDeletePhotoSuccess: Function,
  selectedAlbumId: number
) => {
  try {
    await $.ajax({
      url: `/api/v1/photo-manager/delete_media_of_album`,
      type: "DELETE",
      data: {
        photo_ids: Array.from(selectedMediaId),
        album_id: selectedAlbumId
      }
    });

    // @ts-ignore
    toastr.success("Successfully deleted");
    onDeletePhotoSuccess();

    // Refresh the photos after deletion
  } catch (err) {
    if (err.status === 403) {
      // @ts-ignore
      toastr.error("You don't have permission to delete these photos.");
    } else {
      // @ts-ignore
      toastr.error("Failed to delete photos. Please try again");
    }
    console.error(err);
  }
};

export const handleSave = (
  photoId: number,
  userId: number,
  onSuccess: Function
) => {
  ajax
    .post(addParamToURL(`/api/v1/photos/${photoId}/save`, "user_id", userId))
    .then(res => {
      onSuccess(true);
      //@ts-ignore
      toastr.success("Photo saved", null, { timeOut: 1000 });
    })
    .catch(err => {
      console.error("Error saving photo:", err);
      //@ts-ignore
      toastr.error("Failed to save photo. Please try again.");
    });

  return true;
};

export const handleUnsave = (
  photoId: number,
  userId: number,
  onSuccess: Function
) => {
  ajax
    .delete(
      addParamToURL(`/api/v1/photos/${photoId}/unsave`, "user_id", userId)
    )
    .then(res => {
      onSuccess(false);
      //@ts-ignore
      toastr.success("Photo unsaved", null, { timeOut: 1000 });
      return true;
    })
    .catch(err => {
      console.error("Error unsaving photo:", err);
      //@ts-ignore
      toastr.error("Failed to unsave photo. Please try again.");
    });
};

export const hidePhotos = async (
  selectedMediaId: any,
  hideOrUnhideText: string,
  selectedEventId: number,
  clearSelection: Function,
  fetchAlbumPhotosFunction: Function,
  selectedAlbum: any,
  photoCurrentPage: number
) => {
  try {
    await $.ajax({
      url: `/api/v1/photos/${hideOrUnhideText}`,
      type: "POST",
      data: {
        photo_ids: Array.from(selectedMediaId),
        event_id: selectedEventId
      }
    });

    // @ts-ignore
    toastr.success("Successfully updated");
    clearSelection();
    // Refresh the photos to get updated hidden status
    if (selectedAlbum) {
      fetchAlbumPhotosFunction(selectedAlbum, photoCurrentPage);
    } else {
    }
  } catch (err) {
    // @ts-ignore
    toastr.error("Failed to update");
    console.error(err);
  }
};

export const hidePhoto = async (
  selectedMediaId: any,
  hideOrUnhideText: string,
  selectedEventId: number,
  onSuccess: Function,
  selectedAlbum: any,
  fetchAlbumPhotosFunction: Function,
  photoCurrentPage: number
) => {
  try {
    await $.ajax({
      url: `/api/v1/photos/${hideOrUnhideText}`,
      type: "POST",
      data: {
        photo_ids: [selectedMediaId],
        event_id: selectedEventId
      }
    });

    // @ts-ignore
    toastr.success("Successfully updated");
    // Refresh the photos to get updated hidden status
    onSuccess(hideOrUnhideText === "hide" ? true : false);
    if (selectedAlbum) {
      fetchAlbumPhotosFunction(selectedAlbum, photoCurrentPage);
    }
  } catch (err) {
    // @ts-ignore
    toastr.error("Failed to update");
  }
};

export const handleDownload = async (
  selectedMediaId: Set<number>,
  selectedEventId: number
) => {
  if (selectedMediaId.size === 0) return;

  const photoIds = Array.from(selectedMediaId);
  const params = new URLSearchParams();
  params.append("t", "Gallery");
  params.append("id", selectedEventId.toString());
  photoIds.forEach(id => params.append("photo_ids[]", id.toString()));
  const url = `/api/v1/photo-manager/download?${params.toString()}`;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to fetch download URL");

    const data = await response.json();
    if (data.download_url) {
      window.location.href = data.download_url; // Trigger ZIP download
    }
  } catch (error) {
    console.error("Download failed:", error);
    // @ts-ignore
    toastr.error("Failed to download photos. Please try again", null, 1000);
  }
};

export const fetchEventData = async (
  eventId: number,
  setSelectedEvent: (event: any) => void,
  setLoadingEvent: (loading: boolean) => void
) => {
  try {
    setLoadingEvent(true);
    const response = await fetch(
      `/api/v1/photo-manager/details/?t=Gallery&id=${eventId}`
    );

    if (!response.ok) throw new Error("Failed to fetch event data");

    const data = await response.json();
    setSelectedEvent(data.entities);
  } catch (error) {
    console.error("Error fetching event details:", error);
  } finally {
    setLoadingEvent(false);
  }
};

export const fetchAlbumData = async (
  albumId: number,
  setSelectedAlbum: (album: any) => void,
  setLoadingAlbum: (loading: boolean) => void
) => {
  try {
    setLoadingAlbum(true);
    const response = await fetch(
      `/api/v1/photo-manager/details/?t=Album&id=${albumId}`
    );

    if (!response.ok) throw new Error("Failed to fetch album data");

    const data = await response.json();
    setSelectedAlbum(data.entities);
  } catch (error) {
    console.error("Error fetching album details:", error);
  } finally {
    setLoadingAlbum(false);
  }
};
export const createLibraryFolder = async (
  params: CreateLibraryFolderParamType,
  onCreateLibraryFolderSuccess: (newLibraryFolder: Folder) => void
) => {
  try {
    const response = await $.ajax({
      url: `/api/v1/library_folders`,
      type: "POST",
      data: params
    });
    const newLibraryFolder = response.library_folder;
    onCreateLibraryFolderSuccess(newLibraryFolder);
  } catch (error) {
    console.error("Error creating library folder:", error);
    // @ts-ignore
    toastr.error(
      "Unable to create library folder. Please try again",
      null,
      1000
    );
  }
};

export const deleteFolder = async (folderId: number): Promise<boolean> => {
  try {
    const response = await $.ajax({
      url: `/api/v1/library_folders/${folderId}`,
      type: "DELETE"
    });

    // @ts-ignore
    toastr.success("Successfully deleted");
    return true;
  } catch (err) {
    if (err.status === 403) {
      // @ts-ignore
      toastr.error("You don't have permission to delete this folder.");
    } else {
      // @ts-ignore
      toastr.error("Failed to delete folder. Please try again");
    }

    return false;
  }
};

export const updateFolder = async (
  folderId: number,
  name: string,
  parentFolderId: number | null
) => {
  try {
    await $.ajax({
      url: `/api/v1/library_folders/${folderId}`,
      type: "PATCH",
      data: {
        library_folder: {
          name: name,
          parent_folder_id: parentFolderId
        }
      }
    });

    // @ts-ignore
    toastr.success("Successfully updated");
  } catch (err) {
    if (err.status === 403) {
      // @ts-ignore
      toastr.error("You don't have permission to update this folder.");
    } else {
      // @ts-ignore
      toastr.error("Failed to update folder. Please try again");
    }
    console.error(err);
  }
};

export const moveFolder = async ({
  setLoading,
  src,
  dest
}: {
  setLoading: (val: boolean) => void;
  src: SrcType;
  dest: DestFolderType;
}) => {
  setLoading(true);

  try {
    const requestBody = {
      src: [src.entityType, src.entityId],
      dest: [dest.entityType, dest.entityId]
    };

    const response = await fetch("/api/v1/photo-manager/move", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    if (data.ok) {
      toastr.success("Moved successfully");
    } else {
      toastr.error("Failed to move");
    }
  } catch (error) {
    console.error("Error Moving:", error);
    toastr.error("Failed to Move. Please try again.");
  } finally {
    setLoading(false);
  }
};

// Content Permissions API

export interface ContentPermission {
  id: number;
  granteeType: string;
  granteeId: number;
  granteeName: string;
  granteeEmail: string | null;
  accessLevel: string;
  grantedByName: string | null;
  createdAt: string;
}

export interface AvailableUser {
  id: number;
  name: string;
  email: string;
  type?: "User" | "UserGroup";
  memberCount?: number;
}

export type ContentType = "LibraryFolder" | "Event";
export type AccessLevel = "viewer" | "contributor" | "manager";

export const fetchContentPermissions = async (
  contentType: ContentType,
  contentId: number
): Promise<{
  permissions: ContentPermission[];
  defaultAccessLevel: string | null;
}> => {
  try {
    const response = await fetch(
      `/api/v1/content_permissions?content_type=${contentType}&content_id=${contentId}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Not authorized to manage access");
      }
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return {
      permissions: data.permissions.map((p: any) => ({
        id: p.id,
        granteeType: p.grantee_type,
        granteeId: p.grantee_id,
        granteeName: p.grantee_name,
        granteeEmail: p.grantee_email,
        accessLevel: p.access_level,
        grantedByName: p.granted_by_name,
        createdAt: p.created_at
      })),
      defaultAccessLevel: data.default_access_level
    };
  } catch (error) {
    console.error("Failed to fetch content permissions:", error);
    throw error;
  }
};

export const createContentPermission = async (
  contentType: ContentType,
  contentId: number,
  granteeId: number,
  accessLevel: AccessLevel,
  granteeType: "User" | "UserGroup" = "User"
): Promise<ContentPermission> => {
  try {
    const response = await fetch("/api/v1/content_permissions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        content_type: contentType,
        content_id: contentId,
        grantee_id: granteeId,
        grantee_type: granteeType,
        access_level: accessLevel
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.errors?.join(", ") || "Failed to add permission"
      );
    }

    const data = await response.json();
    const p = data.permission;
    return {
      id: p.id,
      granteeType: p.grantee_type,
      granteeId: p.grantee_id,
      granteeName: p.grantee_name,
      granteeEmail: p.grantee_email,
      accessLevel: p.access_level,
      grantedByName: p.granted_by_name,
      createdAt: p.created_at
    };
  } catch (error) {
    console.error("Failed to create content permission:", error);
    throw error;
  }
};

export const updateContentPermission = async (
  contentType: ContentType,
  contentId: number,
  permissionId: number,
  accessLevel: AccessLevel
): Promise<ContentPermission> => {
  try {
    const response = await fetch(
      `/api/v1/content_permissions/${permissionId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          content_type: contentType,
          content_id: contentId,
          access_level: accessLevel
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.errors?.join(", ") || "Failed to update permission"
      );
    }

    const data = await response.json();
    const p = data.permission;
    return {
      id: p.id,
      granteeType: p.grantee_type,
      granteeId: p.grantee_id,
      granteeName: p.grantee_name,
      granteeEmail: p.grantee_email,
      accessLevel: p.access_level,
      grantedByName: p.granted_by_name,
      createdAt: p.created_at
    };
  } catch (error) {
    console.error("Failed to update content permission:", error);
    throw error;
  }
};

export const deleteContentPermission = async (
  contentType: ContentType,
  contentId: number,
  permissionId: number
): Promise<void> => {
  try {
    const response = await fetch(
      `/api/v1/content_permissions/${permissionId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          content_type: contentType,
          content_id: contentId
        })
      }
    );

    if (!response.ok) {
      throw new Error("Failed to remove permission");
    }
  } catch (error) {
    console.error("Failed to delete content permission:", error);
    throw error;
  }
};

export const updateDefaultAccessLevel = async (
  contentType: ContentType,
  contentId: number,
  defaultAccessLevel: AccessLevel | null
): Promise<{ defaultAccessLevel: string | null; message: string }> => {
  try {
    const response = await fetch(
      "/api/v1/content_permissions/update_default_access",
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          content_type: contentType,
          content_id: contentId,
          default_access_level: defaultAccessLevel
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to update default access");
    }

    const data = await response.json();
    return {
      defaultAccessLevel: data.default_access_level,
      message: data.message
    };
  } catch (error) {
    console.error("Failed to update default access level:", error);
    throw error;
  }
};

export const fetchAvailableUsers = async (
  contentType: ContentType,
  contentId: number,
  query: string = ""
): Promise<AvailableUser[]> => {
  try {
    const params = new URLSearchParams({
      content_type: contentType,
      content_id: contentId.toString()
    });
    if (query) {
      params.append("q", query);
    }

    const response = await fetch(
      `/api/v1/content_permissions/available_users?${params.toString()}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data.users;
  } catch (error) {
    console.error("Failed to fetch available users:", error);
    return [];
  }
};

export const fetchUserGroups = async (): Promise<
  { id: number; name: string; memberCount?: number }[]
> => {
  try {
    const response = await fetch("/user_groups", {
      headers: {
        "Cache-Control": "no-store",
        Pragma: "no-cache"
      },
      cache: "no-store"
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch user groups:", error);
    return [];
  }
};

export const fetchMyAccess = async (
  contentType: ContentType,
  contentId: number
): Promise<{
  accessLevel: string | null;
  canView: boolean;
  canUpload: boolean;
  canCurate: boolean;
  canManageAccess: boolean;
  canCreateEvents: boolean;
  canCreateFolders: boolean;
}> => {
  try {
    const response = await fetch(
      `/api/v1/content_permissions/my_access?content_type=${contentType}&content_id=${contentId}`,
      {
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store"
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return {
      accessLevel: data.access_level,
      canView: data.can_view,
      canUpload: data.can_upload,
      canCurate: data.can_curate,
      canManageAccess: data.can_manage_access,
      canCreateEvents: data.can_create_events,
      canCreateFolders: data.can_create_folders
    };
  } catch (error) {
    console.error("Failed to fetch my access:", error);
    return {
      accessLevel: null,
      canView: false,
      canUpload: false,
      canCurate: false,
      canManageAccess: false,
      canCreateEvents: false,
      canCreateFolders: false
    };
  }
};

// Library Photos Filtering APIs

export type EntityType =
  | "tag"
  | "person"
  | "group"
  | "event"
  | "album"
  | "folder";

export interface LibraryPhoto {
  id: number;
  thumbnailURL: string;
  fullURL: string;
  timeTaken: string | null;
  dateTimeTaken: string | null;
  albumId: number;
  albumName: string;
  eventId: number;
  eventName: string;
  hidden: boolean;
  contentType: string;
  videoContent: boolean;
  videoMuxPlaybackId: string | null;
  videoMuxId: string | null;
  videoDuration: number | null;
  videoStartTime?: number;
  fileName: string;
  folderPath: string[];
  redactionChecked: boolean;
  flaggedForModeration?: boolean;
  tags: Array<{ id: number; name: string }>;
  people: Array<{
    id: number;
    firstName?: string;
    lastName?: string;
    extId?: string;
    photoURL?: string | null;
    photoPermissionExpiresOn?: string | null;
    permissionExpiryStatus?: {
      status: "expired" | "warning" | "valid";
      message: string | null;
    } | null;
    className?: string | null;
    redacted?: boolean;
    permissions?: Array<{
      label: string;
      value: boolean | null;
    }>;
  }>;
  permissions?: {
    redacted: boolean;
    byAttribute: Array<{
      attributeId: number;
      attributeLabel: string;
      value: boolean | null;
    }>;
  };
  eventPermissions?: {
    isAuthorizedForDirectDownloads: boolean;
  };
  albumPermissions?: {
    canCurrentUserDelete: boolean;
  };
  canDelete: boolean;
  canHide: boolean;
  canTag: boolean;
}

export interface FilteredPhotosResponse {
  photos: LibraryPhoto[];
  meta: {
    page: number;
    perPage: number;
    totalCount: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface PermissionFilter {
  attribute_id: number;
  value: "true" | "false";
}

export interface FilterOptions {
  person_ids?: number[];
  group_ids?: number[];
  tag_ids?: number[];
  event_ids?: number[];
  album_ids?: number[];
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
  reference_image?: string;
  filter_reference_image?: string;
  permission_filters?: PermissionFilter[];
  redacted_photo?: "true" | "false";
}

export interface FilterOptionsResponse {
  events: Array<{ id: number; name: string; photo_count: number }>;
  albums: Array<{
    id: number;
    name: string;
    event_name: string;
    photo_count: number;
  }>;
  tags: Array<{ id: number; name: string; photo_count: number }>;
  people: Array<{ id: number; name: string; photo_count: number }>;
  groups: Array<{ id: number; name: string; member_count: number }>;
  date_range: { earliest: string; latest: string };
  photo_permission_attributes?: Array<{ id: number; label: string }>;
  has_redacted_participants?: boolean;
  has_matched_people?: boolean;
}

export const fetchFilteredPhotos = async (
  entityType: EntityType,
  entityId: number,
  filters: FilterOptions = {},
  signal?: AbortSignal
): Promise<FilteredPhotosResponse> => {
  try {
    const params = new URLSearchParams();

    // Only add entity filters if entityId is valid (not -1 for global search)
    if (entityId !== -1) {
      params.append("entity_type", entityType);
      params.append("entity_id", entityId.toString());
    }

    // Add optional filters
    if (filters.person_ids) {
      filters.person_ids.forEach(id => {
        params.append("person_ids[]", id.toString());
      });
    }
    if (filters.group_ids) {
      filters.group_ids.forEach(id => {
        params.append("group_ids[]", id.toString());
      });
    }
    if (filters.tag_ids) {
      filters.tag_ids.forEach(id => {
        params.append("tag_ids[]", id.toString());
      });
    }
    if (filters.event_ids) {
      filters.event_ids.forEach(id => {
        params.append("event_ids[]", id.toString());
      });
    }
    if (filters.album_ids) {
      filters.album_ids.forEach(id => {
        params.append("album_ids[]", id.toString());
      });
    }
    if (filters.date_from) {
      params.append("date_from", filters.date_from);
    }
    if (filters.date_to) {
      params.append("date_to", filters.date_to);
    }
    if (filters.page) {
      params.append("page", filters.page.toString());
    }
    if (filters.per_page) {
      params.append("per_page", filters.per_page.toString());
    }
    if (filters.reference_image) {
      params.append("reference_image", filters.reference_image);
    }
    if (filters.filter_reference_image) {
      params.append("filter_reference_image", filters.filter_reference_image);
    }
    if (filters.permission_filters && filters.permission_filters.length > 0) {
      filters.permission_filters.forEach(filter => {
        params.append(
          "permission_filters[][attribute_id]",
          filter.attribute_id.toString()
        );
        params.append("permission_filters[][value]", filter.value);
      });
    }
    if (filters.redacted_photo) {
      params.append("redacted_photo", filters.redacted_photo);
    }

    const response = await fetch(
      `/api/v1/library/photos?${params.toString()}`,
      {
        credentials: "same-origin",
        headers: {
          "Cache-Control": "no-store",
          Pragma: "no-cache"
        },
        cache: "no-store",
        signal
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();

    return {
      photos: data.photos,
      meta: data.meta
    };
  } catch (error) {
    console.error("Failed to fetch filtered photos:", error);
    throw error;
  }
};

export const fetchFilterOptions = async (
  entityType?: EntityType,
  entityId?: number,
  referenceImage?: string
): Promise<FilterOptionsResponse> => {
  try {
    const params = new URLSearchParams();

    if (entityType && entityId !== undefined && entityId !== -1) {
      params.append("entity_type", entityType);
      params.append("entity_id", entityId.toString());
    }

    if (referenceImage) {
      params.append("reference_image", referenceImage);
    }

    const queryString = params.toString();
    const url = queryString
      ? `/api/v1/library/photos/filter-options?${queryString}`
      : `/api/v1/library/photos/filter-options`;

    const response = await fetch(url, {
      credentials: "same-origin",
      headers: {
        "Cache-Control": "no-store",
        Pragma: "no-cache"
      },
      cache: "no-store"
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to fetch filter options:", error);
    throw error;
  }
};
